// FILE: WarehouseInventoryList.js
// CHANGELOG:
// 1) Restored the stock-by-product-type bar chart.
// 2) Moved the daily stock movement chart into the right-side secondary card slot.
// 3) Kept KPI cards, table tabs, modal details, and summary card.

import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../layout/head/Head";
import TablePageHeading from "../components/page_heading/TablePageHeading";
import Content from "../../../layout/content/Content";
import CardTabs from "../components/cards/CardTabs";
import { motion } from "framer-motion";
import TableList from "../components/table/TableList";
import { Col, Row } from "../../../components/Component";

import {
  RETRIEVE_STOCK_LEVELS_SUMMARY_DATA_API,
  RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_HISTORY_DETAILS_API,
} from "../../../api/api_data";
import { makeEmployeeDashboardApiRequest } from "../../../api/api_helper";

import {
  fail_retrieve_warehouse_inventory_summary_data,
  loading_retrieve_warehouse_inventory_summary_data,
  reset_retrieve_warehouse_inventory_summary_data,
  success_retrieve_warehouse_inventory_summary_data,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_warehouse_inventory_summary_data_Slice";

import {
  RETRIEVE_WAREHOUSE_INVNENTORY_LIST_ACCESS_DENIED_MSG,
  RETRIEVE_WAREHOUSE_INVNENTORY_LIST_ERR_MSG,
} from "../../../constants/success_error_messages_consts";

import { useDispatch, useSelector } from "react-redux";
import { messageToast } from "../../../utils/Utils";
import { employee_dashboard_actions_helper } from "../../../actions/actions_helper";
import * as app_consts from "../../../constants/react_app_consts";
import ModalDetails from "../components/modal/modal_details/ModalDetails";
import { Spinner } from "reactstrap";
import { formatScientificNotation } from "../../../utils/functionalUtils";
import StockMovementChart from "./StockMovementChart";
import {
  BiErrorCircle,
  BiPackage,
  BiCategoryAlt,
  BiCube,
  BiLayer,
  BiBarChartAlt2,
} from "react-icons/bi";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const type = app_consts.WAREHOUSE_INVENTORY_LIST;

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safePct = (num, den) => {
  const n = toNum(num);
  const d = toNum(den);
  if (!d || d <= 0) return 0;
  return (n / d) * 100;
};

const STOCK_STATUS = {
  NEGATIVE: "Negative",
  ZERO: "Zero Stock",
  AVAILABLE: "Available",
};

const CHART_COLORS = {
  physical: "#3b82f6",
  virtual: "#14b8a6",
  pie: ["#3b82f6", "#14b8a6"],
};

const MOVEMENT_SERIES_COLORS = ["#6576ff", "#1ee0ac", "#f4bd0e"];
const OUTBOUND_MOVEMENT_KEYWORDS = ["remove", "out", "decrease", "deduct", "subtract", "issued", "dispatch"];

const getStockSeriesLabel = (seriesName) => {
  const normalizedName = String(seriesName || "").trim().toLowerCase();

  if (normalizedName === "physical") return "Physical";
  if (normalizedName === "virtual") return "Virtual";

  return seriesName || "--";
};

const toDateKey = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toSeriesKey = (value, index) =>
  `${String(value || "category")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "category"}_${index}`;

const buildMovementDateRange = (dates) => {
  const validDates = (dates || []).filter(Boolean).sort((left, right) => left.localeCompare(right));

  if (validDates.length === 0) {
    return [];
  }

  const start = new Date(`${validDates[0]}T00:00:00Z`);
  const end = new Date(`${validDates[validDates.length - 1]}T00:00:00Z`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return validDates;
  }

  const results = [];
  const cursor = new Date(start.getTime());

  while (cursor <= end) {
    const year = cursor.getUTCFullYear();
    const month = String(cursor.getUTCMonth() + 1).padStart(2, "0");
    const day = String(cursor.getUTCDate()).padStart(2, "0");
    results.push(`${year}-${month}-${day}`);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return results;
};

const matchesOutboundKeyword = (value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return OUTBOUND_MOVEMENT_KEYWORDS.some((keyword) => normalizedValue.includes(keyword));
};

const isOutboundMovement = (entry) => {
  if (matchesOutboundKeyword(entry?.adjustment_type) || matchesOutboundKeyword(entry?.type)) {
    return true;
  }

  const historyRows = Array.isArray(entry?.inventory_history) ? entry.inventory_history : [];
  return historyRows.some(
    (historyItem) =>
      matchesOutboundKeyword(historyItem?.adjustment_type) ||
      matchesOutboundKeyword(historyItem?.type) ||
      matchesOutboundKeyword(historyItem?.message),
  );
};

const WarehouseInventoryList = () => {
  const dispatch = useDispatch();

  const [summaryData, setSummaryData] = useState({});
  const [flattenedProducts, setFlattenedProducts] = useState([]);
  const [stockMovementSeries, setStockMovementSeries] = useState([]);
  const [stockOutSeries, setStockOutSeries] = useState([]);
  const [productHistoryExportRows, setProductHistoryExportRows] = useState([]);
  const [stockInExportRows, setStockInExportRows] = useState([]);
  const [stockOutExportRows, setStockOutExportRows] = useState([]);
  const [stockMovementLoading, setStockMovementLoading] = useState(false);
  const [stockMovementError, setStockMovementError] = useState("");

  const [allRows, setAllRows] = useState([]);
  const [physicalRows, setPhysicalRows] = useState([]);
  const [virtualRows, setVirtualRows] = useState([]);
  const [zeroRows, setZeroRows] = useState([]);
  const [negativeRows, setNegativeRows] = useState([]);

  const [error, setError] = useState("");
  const [dataFetched, setDataFetched] = useState(false);

  const [activeCardTab, setActiveCardTab] = useState("All");
  const [openModalDetails, setOpendModalDetails] = useState(false);
  const [selectedData, setSelectedData] = useState("");
  const [detail, setDetail] = useState(null);

  const cardMotion = useMemo(
    () => ({
      hidden: { opacity: 0, y: 12, scale: 0.985 },
      show: (i = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, delay: i * 0.08, ease: "easeOut" },
      }),
    }),
    [],
  );

  const hoverMotion = useMemo(
    () => ({
      whileHover: { y: -6, transition: { duration: 0.18, ease: "easeOut" } },
      whileTap: { scale: 0.99 },
    }),
    [],
  );

  useEffect(() => {
    retrieve_warehouse_inventory_summary_func();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieve_warehouse_inventory_summary_func = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_warehouse_inventory_summary_data,
        success_retrieve_warehouse_inventory_summary_data,
        fail_retrieve_warehouse_inventory_summary_data,
        RETRIEVE_WAREHOUSE_INVNENTORY_LIST_ERR_MSG,
        null,
        app_consts.GET,
        RETRIEVE_STOCK_LEVELS_SUMMARY_DATA_API,
        null,
        RETRIEVE_WAREHOUSE_INVNENTORY_LIST_ACCESS_DENIED_MSG,
        null,
        null,
        true,
      ),
    );
  };

  const refreshList = () => {
    setError("");
    setDataFetched(false);
    setSummaryData({});
    setFlattenedProducts([]);
    setAllRows([]);
    setPhysicalRows([]);
    setVirtualRows([]);
    setZeroRows([]);
    setNegativeRows([]);
    setStockMovementSeries([]);
    setStockOutSeries([]);
    setProductHistoryExportRows([]);
    setStockInExportRows([]);
    setStockOutExportRows([]);
    setStockMovementError("");
    setStockMovementLoading(false);
    setDetail(null);
    setSelectedData("");
    retrieve_warehouse_inventory_summary_func();
  };

  const retrieve_warehouse_inventory_summary_data = useSelector(
    (state) => state.retrieve_warehouse_inventory_summary_data.retrieve_warehouse_inventory_summary_data,
  );

  const isLoading = retrieve_warehouse_inventory_summary_data.status === app_consts.LOADING;

  useEffect(() => {
    if (retrieve_warehouse_inventory_summary_data.status === app_consts.IDLE) return;

    if (retrieve_warehouse_inventory_summary_data.status === app_consts.LOADING) {
      setError("");
      return;
    }

    if (retrieve_warehouse_inventory_summary_data.status === app_consts.FAIL) {
      setError(retrieve_warehouse_inventory_summary_data.message);
      messageToast(RETRIEVE_WAREHOUSE_INVNENTORY_LIST_ERR_MSG, app_consts.FAIL);
      dispatch(reset_retrieve_warehouse_inventory_summary_data());
      return;
    }

    if (retrieve_warehouse_inventory_summary_data.status === app_consts.SUCCESS) {
      const payload = retrieve_warehouse_inventory_summary_data.data || {};
      setSummaryData(payload);
      setDataFetched(true);

      const groups = payload?.products_list || [];
      const flat = [];

      if (Array.isArray(groups)) {
        groups.forEach((g) => {
          const groupName = g?.group_name || "";
          const prods = Array.isArray(g?.products) ? g.products : [];

          prods.forEach((p) => {
            const physical = toNum(p?.sum_total_physical_in_mt);
            const virtual = toNum(p?.sum_total_virtual_in_mt);
            const total = physical + virtual;

            let status = STOCK_STATUS.AVAILABLE;
            if (physical < 0 || virtual < 0) status = STOCK_STATUS.NEGATIVE;
            else if (physical === 0 && virtual === 0) status = STOCK_STATUS.ZERO;

            flat.push({
              ...p,
              group_name: groupName,
              product_id: p?.product_id,
              sum_total_physical_in_mt: physical,
              sum_total_virtual_in_mt: virtual,
              total_mt: total,
              stock_status: status,
            });
          });
        });
      }

      setFlattenedProducts(flat);
      setAllRows(flat);
      setPhysicalRows(flat.filter((x) => toNum(x.sum_total_physical_in_mt) > 0));
      setVirtualRows(flat.filter((x) => toNum(x.sum_total_virtual_in_mt) > 0));
      setZeroRows(
        flat.filter((x) => toNum(x.sum_total_physical_in_mt) === 0 && toNum(x.sum_total_virtual_in_mt) === 0),
      );
      setNegativeRows(
        flat.filter((x) => toNum(x.sum_total_physical_in_mt) < 0 || toNum(x.sum_total_virtual_in_mt) < 0),
      );

      dispatch(reset_retrieve_warehouse_inventory_summary_data());
      return;
    }

    dispatch(reset_retrieve_warehouse_inventory_summary_data());
  }, [retrieve_warehouse_inventory_summary_data, dispatch]);

  useEffect(() => {
    if (selectedData) {
      const found = (flattenedProducts || []).find((d) => d.product_id === selectedData);
      setDetail(found || null);
    } else {
      setDetail(null);
    }
  }, [selectedData, flattenedProducts]);

  useEffect(() => {
    let ignore = false;

    const fetchStockMovement = async () => {
      const productsToFetch = (flattenedProducts || []).filter((item) => item?.product_id);

      if (!dataFetched || productsToFetch.length === 0) {
        setStockMovementSeries([]);
        setStockOutSeries([]);
        setProductHistoryExportRows([]);
        setStockInExportRows([]);
        setStockOutExportRows([]);
        setStockMovementError("");
        setStockMovementLoading(false);
        return;
      }

      setStockMovementLoading(true);
      setStockMovementError("");

      const groupedMovement = {};
      const groupedStockOut = {};
      const trackedDates = new Set();
      const historyRows = [];
      let successfulRequests = 0;

      try {
        for (let index = 0; index < productsToFetch.length; index += 8) {
          const batch = productsToFetch.slice(index, index + 8);
          const responses = await Promise.allSettled(
            batch.map(async (product) => {
              const apiResponse = await makeEmployeeDashboardApiRequest(
                app_consts.GET,
                `${RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_HISTORY_DETAILS_API}${product.product_id}`,
              );

              return {
                product,
                payload: apiResponse?.data?.data || apiResponse?.data || {},
              };
            }),
          );

          if (ignore) {
            return;
          }

          responses.forEach((result) => {
            if (result.status !== "fulfilled") {
              return;
            }

            successfulRequests += 1;

            const { product, payload } = result.value;
            const categoryLabel =
              String(product?.group_name || product?.product_type || "Uncategorized").trim() || "Uncategorized";
            const productData = payload?.data?.product || payload?.product || payload?.data || payload || {};
            const inventory = Array.isArray(productData?.inventory) ? productData.inventory : [];

            inventory.forEach((entry) => {
              const dateKey = toDateKey(
                entry?.inventory_history?.[0]?.timestamp || entry?.timestamp || entry?.date || entry?.inserted_at,
              );
              const amount = toNum(entry?.capacity ?? entry?.tonnage ?? entry?.quantity);
              const historyItem = Array.isArray(entry?.inventory_history) ? entry.inventory_history[0] || {} : {};
              const movementTimestamp =
                historyItem?.timestamp || entry?.timestamp || entry?.date || entry?.inserted_at || "";
              if (!dateKey || !amount) {
                return;
              }

              trackedDates.add(dateKey);

              const isOutbound = isOutboundMovement(entry);
              const signedAmount = isOutbound ? -amount : amount;
              const movementType =
                String(
                  entry?.adjustment_type ||
                    entry?.type ||
                    historyItem?.adjustment_type ||
                    historyItem?.type ||
                    ""
                ).trim() || (isOutbound ? "subtract" : "add");
              const reasonMessage =
                String(historyItem?.message || entry?.reason || entry?.message || "").trim();

              historyRows.push({
                product_id: product?.product_id || "",
                product_name: product?.product_name || "",
                product_group: categoryLabel,
                product_type: product?.product_type || "",
                movement_date: dateKey,
                movement_timestamp: movementTimestamp,
                movement_direction: isOutbound ? "outbound" : "inbound",
                movement_type: movementType,
                movement_mt: Number(amount.toFixed(2)),
                signed_movement_mt: Number(signedAmount.toFixed(2)),
                inventory_type:
                  entry?.inventory_type ||
                  entry?.inventory_type_name ||
                  entry?.ticket_type ||
                  historyItem?.inventory_type ||
                  "",
                packet_size: entry?.packet_size || entry?.uom_description || product?.packet_size || "",
                unit_measurement:
                  entry?.unit_measurement_abbrev ||
                  entry?.unit_measurement ||
                  product?.unit_measurement_abbrev ||
                  "",
                reason_message: reasonMessage,
              });

              if (!isOutbound) {
                if (!groupedMovement[categoryLabel]) {
                  groupedMovement[categoryLabel] = {};
                }

                groupedMovement[categoryLabel][dateKey] =
                  toNum(groupedMovement[categoryLabel][dateKey]) + amount;
              }

              if (signedAmount < 0) {
                if (!groupedStockOut[categoryLabel]) {
                  groupedStockOut[categoryLabel] = {};
                }

                groupedStockOut[categoryLabel][dateKey] =
                  toNum(groupedStockOut[categoryLabel][dateKey]) + Math.abs(signedAmount);
              }
            });
          });
        }

        if (ignore) {
          return;
        }

        const orderedLabels = Object.keys(groupedMovement).sort((left, right) => left.localeCompare(right));
        const outboundLabels = Object.keys(groupedStockOut).sort((left, right) => left.localeCompare(right));
        const dateRange = buildMovementDateRange(Array.from(trackedDates));

        const nextSeries = orderedLabels.map((label, index) => ({
          key: toSeriesKey(label, index),
          label,
          color: MOVEMENT_SERIES_COLORS[index % MOVEMENT_SERIES_COLORS.length],
          background: `${MOVEMENT_SERIES_COLORS[index % MOVEMENT_SERIES_COLORS.length]}1A`,
          points: Object.entries(groupedMovement[label] || {})
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([date, value]) => ({
              date,
              value: Number(toNum(value).toFixed(2)),
            })),
        }));

        const nextStockOutSeries = outboundLabels.map((label, index) => ({
          key: toSeriesKey(label, index),
          label,
          color: MOVEMENT_SERIES_COLORS[index % MOVEMENT_SERIES_COLORS.length],
          background: `${MOVEMENT_SERIES_COLORS[index % MOVEMENT_SERIES_COLORS.length]}1A`,
          points: dateRange.map((date) => ({
            date,
            value: Number(toNum(groupedStockOut[label]?.[date]).toFixed(2)),
          })),
        }));

        const nextStockInExportRows = nextSeries.flatMap((series) =>
          (series.points || [])
            .filter((point) => toNum(point?.value) > 0)
            .map((point) => ({
              product_group: series.label,
              movement_date: point.date,
              movement_direction: "inbound",
              movement_type: "daily_stock_in",
              movement_mt: Number(toNum(point.value).toFixed(2)),
              signed_movement_mt: Number(toNum(point.value).toFixed(2)),
            }))
        );

        const nextStockOutExportRows = nextStockOutSeries.flatMap((series) =>
          (series.points || [])
            .filter((point) => toNum(point?.value) > 0)
            .map((point) => ({
              product_group: series.label,
              movement_date: point.date,
              movement_direction: "outbound",
              movement_type: "daily_stock_out",
              movement_mt: Number(toNum(point.value).toFixed(2)),
              signed_movement_mt: Number((-toNum(point.value)).toFixed(2)),
            }))
        );

        setStockMovementSeries(nextSeries);
        setStockOutSeries(nextStockOutSeries);
        setProductHistoryExportRows(historyRows);
        setStockInExportRows(nextStockInExportRows);
        setStockOutExportRows(nextStockOutExportRows);

        if (nextSeries.length === 0 && successfulRequests === 0) {
          setStockMovementError("Could not load daily stock movement data.");
        }
      } catch (movementError) {
        console.error("Failed to build stock movement chart.", movementError);

        if (!ignore) {
          setStockMovementSeries([]);
          setStockOutSeries([]);
          setProductHistoryExportRows([]);
          setStockInExportRows([]);
          setStockOutExportRows([]);
          setStockMovementError("Could not load daily stock movement data.");
        }
      } finally {
        if (!ignore) {
          setStockMovementLoading(false);
        }
      }
    };

    fetchStockMovement();

    return () => {
      ignore = true;
    };
  }, [dataFetched, flattenedProducts]);

  const tabs = useMemo(
    () => [
      { tabName: "All" },
      { tabName: "Physical" },
      { tabName: "Virtual" },
      { tabName: "Zero Stock" },
      { tabName: "Negative" },
    ],
    [],
  );

  const totalPhysicalMt = toNum(summaryData?.total_physical_mt);
  const totalVirtualMt = toNum(summaryData?.total_virtual_mt);

  const productTypesCount = Array.isArray(summaryData?.products_list) ? summaryData.products_list.length : 0;

  const productsCount = flattenedProducts.length;

  const percentValues = Array.isArray(summaryData?.percent_tonnage_values) ? summaryData.percent_tonnage_values : [];

  const physicalDist = useMemo(() => {
    return percentValues
      .filter((x) => (x?.inventory_type || "").toLowerCase() === "physical")
      .map((x) => {
        const tonnage = toNum(x?.tonnage);
        const pctFromApi = x?.percentage;
        const pct =
          pctFromApi === null || pctFromApi === undefined ? safePct(tonnage, totalPhysicalMt) : toNum(pctFromApi);

        return {
          product_type: x?.product_type || "N/A",
          tonnage,
          percentage: pct,
        };
      })
      .sort((a, b) => b.tonnage - a.tonnage);
  }, [percentValues, totalPhysicalMt]);

  const virtualDist = useMemo(() => {
    return percentValues
      .filter((x) => (x?.inventory_type || "").toLowerCase() === "virtual")
      .map((x) => {
        const tonnage = toNum(x?.tonnage);
        const pctFromApi = x?.percentage;
        const pct =
          pctFromApi === null || pctFromApi === undefined ? safePct(tonnage, totalVirtualMt) : toNum(pctFromApi);

        return {
          product_type: x?.product_type || "N/A",
          tonnage,
          percentage: pct,
        };
      })
      .sort((a, b) => b.tonnage - a.tonnage);
  }, [percentValues, totalVirtualMt]);

  const chartData = useMemo(() => {
    const map = {};

    physicalDist.forEach((item) => {
      const key = item.product_type || "N/A";
      if (!map[key]) {
        map[key] = {
          product_type: key,
          physical: 0,
          virtual: 0,
        };
      }
      map[key].physical = toNum(item.tonnage);
    });

    virtualDist.forEach((item) => {
      const key = item.product_type || "N/A";
      if (!map[key]) {
        map[key] = {
          product_type: key,
          physical: 0,
          virtual: 0,
        };
      }
      map[key].virtual = toNum(item.tonnage);
    });

    return Object.values(map).sort(
      (a, b) => toNum(b.physical) + toNum(b.virtual) - (toNum(a.physical) + toNum(a.virtual)),
    );
  }, [physicalDist, virtualDist]);

  const getActiveTableData = () => {
    if (activeCardTab === "All") return allRows;
    if (activeCardTab === "Physical") return physicalRows;
    if (activeCardTab === "Virtual") return virtualRows;
    if (activeCardTab === "Zero Stock") return zeroRows;
    if (activeCardTab === "Negative") return negativeRows;
    return allRows;
  };

  const renderTable = () => {
    const commonProps = {
      type,
      rowOptionsData: {},
      setOpendModalDetails,
      setSelectedData,
    };

    return <TableList {...commonProps} data={getActiveTableData()} />;
  };

  const IconCard = ({ icon: Icon, title, amount }) => {
    return (
      <div className="card card-bordered h-100">
        <div className="card-inner">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="text-soft">{title}</div>
            <Icon className="fs-4 text-primary" />
          </div>
          <div className="fs-22px fw-bold">{amount}</div>
        </div>
      </div>
    );
  };

  const StockChartCard = () => {
    return (
      <div className="card card-bordered h-100">
        <div className="card-inner border-bottom">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div className="fs-15px fw-bold">Stock by Product Type</div>
              <div className="text-soft small">Physical vs Virtual tonnage</div>
            </div>
            <BiBarChartAlt2 className="fs-4 text-primary" />
          </div>
        </div>

        <div className="card-inner">
          {chartData.length === 0 ? (
            <div className="text-soft">No chart data available.</div>
          ) : (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="product_type" tick={{ fill: "#475569", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#475569", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                    }}
                    formatter={(value, name) => [
                      `${toNum(value).toFixed(2)} MT`,
                      getStockSeriesLabel(name),
                    ]}
                    labelFormatter={(label) => `Product Type: ${label}`}
                  />
                  <Legend />
                  <Bar
                    dataKey="physical"
                    name="Physical"
                    stackId="a"
                    fill={CHART_COLORS.physical}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="virtual"
                    name="Virtual"
                    stackId="a"
                    fill={CHART_COLORS.virtual}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    );
  };

  const csvExportData = useMemo(
    () => ({
      stockSnapshots: flattenedProducts,
      productHistory: productHistoryExportRows,
      stockInMovement: stockInExportRows,
      stockOutMovement: stockOutExportRows,
    }),
    [flattenedProducts, productHistoryExportRows, stockInExportRows, stockOutExportRows]
  );

  return (
    <>
      <Head title="Stock Levels" />
      <Content>
        <TablePageHeading
          title="Stock Levels"
          refresh={!isLoading}
          refreshFunc={refreshList}
          csv_export={true}
          page_type={type}
          pageData={csvExportData}
        />

        {isLoading ? (
          <div className="card card-bordered">
            <div className="card-inner py-5">
              <div className="d-flex flex-column align-center justify-center">
                <Spinner />
                <div className="fs-15px mt-2 text-soft">Loading Stock Levels Data…</div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card card-bordered">
            <div className="card-inner py-5 text-center">
              <BiErrorCircle className="text-danger fs-3" />
              <div className="mt-2 fw-bold text-soft">{error}</div>
              <button className="btn btn-primary mt-3" onClick={refreshList} type="button">
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <Row className="g-gs">
            <Col xs="12">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                <Row className="g-gs">
                  <Col xxl="3" sm="6">
                    <motion.div
                      className="h-100"
                      variants={cardMotion}
                      initial="hidden"
                      animate={dataFetched ? "show" : "hidden"}
                      custom={0}
                      {...hoverMotion}
                    >
                      <IconCard
                        icon={BiCube}
                        title="Total Physical (MT)"
                        amount={formatScientificNotation(`${totalPhysicalMt} MT`)}
                      />
                    </motion.div>
                  </Col>

                  <Col xxl="3" sm="6">
                    <motion.div
                      className="h-100"
                      variants={cardMotion}
                      initial="hidden"
                      animate={dataFetched ? "show" : "hidden"}
                      custom={1}
                      {...hoverMotion}
                    >
                      <IconCard
                        icon={BiLayer}
                        title="Total Virtual (MT)"
                        amount={formatScientificNotation(`${totalVirtualMt} MT`)}
                      />
                    </motion.div>
                  </Col>

                  <Col xxl="3" sm="6">
                    <motion.div
                      className="h-100"
                      variants={cardMotion}
                      initial="hidden"
                      animate={dataFetched ? "show" : "hidden"}
                      custom={2}
                      {...hoverMotion}
                    >
                      <IconCard
                        icon={BiCategoryAlt}
                        title="Product Types"
                        amount={formatScientificNotation(`${productTypesCount}`)}
                      />
                    </motion.div>
                  </Col>

                  <Col xxl="3" sm="6">
                    <motion.div
                      className="h-100"
                      variants={cardMotion}
                      initial="hidden"
                      animate={dataFetched ? "show" : "hidden"}
                      custom={3}
                      {...hoverMotion}
                    >
                      <IconCard
                        icon={BiPackage}
                        title="Products"
                        amount={formatScientificNotation(`${productsCount}`)}
                      />
                    </motion.div>
                  </Col>
                </Row>
              </motion.div>
            </Col>

            <Col xxl="8" md="12">
              <StockChartCard />
            </Col>

            <Col xxl="4" md="12">
              <div className="card card-bordered h-100">
                <div className="card-inner border-bottom">
                  <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fs-15px fw-bold">Daily Stock In</div>
                        <div className="text-soft small">Inbound stock trends by product group</div>
                      </div>
                  </div>
                </div>

                <div className="card-inner">
                  <StockMovementChart
                    series={stockMovementSeries}
                    loading={stockMovementLoading}
                    error={stockMovementError}
                    defaultHeading="Daily Stock In"
                    loadingMessage="Loading daily stock in..."
                    emptyMessage="No daily stock-in data available."
                    yAxisTitle="Stock In (MT)"
                  />
                </div>
              </div>
            </Col>

            <Col xxl="8">
              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <div className="fs-15px fw-bold">Stock Levels</div>
                    </div>

                    <div className="ms-auto">
                      <CardTabs
                        cardTabs={tabs}
                        activeCardTab={activeCardTab}
                        setActiveCardTab={setActiveCardTab}
                        dataFetched={dataFetched}
                      />
                    </div>
                  </div>
                </div>

                <div className="card-inner p-0" style={{ background: "transparent", boxShadow: "none" }}>
                  <div className="px-3 pb-3" style={{ background: "transparent" }}>
                    {renderTable()}
                  </div>
                </div>
              </div>

              <ModalDetails
                modalOpen={openModalDetails}
                setModalOpen={setOpendModalDetails}
                modalTitle={`${detail ? `${detail?.product_name}'s Stock Summary` : ""}`}
                modalType={type}
                modalData={detail}
              />
            </Col>

            <Col xxl="4" md="12">
              <div className="card card-bordered h-100">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Daily Stock Out</div>
                  <div className="text-soft small">Outbound stock trends by product group</div>
                </div>

                <div className="card-inner">
                  <StockMovementChart
                    series={stockOutSeries}
                    loading={stockMovementLoading}
                    error={stockMovementError}
                    defaultHeading="Daily Stock Out"
                    loadingMessage="Loading daily stock out..."
                    emptyMessage="No stock-out data available."
                    yAxisTitle="Stock Out (MT)"
                    chartHeight={380}
                  />
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Content>
    </>
  );
};

export default WarehouseInventoryList;
